import { MessageType, PushNotificationEventType } from '@/enums';
import type { InputLiveStatusResponse } from '@/models/input';
import { FuelToolsApiService } from '@/services/FuelToolsApi/fuelToolsApi.service';

export interface LiveInputStatusUpdatePayload {
  inputId: string;
  overallStatus?: string;
}

const readOverallStatus = (data?: Record<string, unknown> | null): string | undefined => {
  if (!data) {
    return undefined;
  }
  return (data.overallStatus ?? data.OverallStatus ?? data.status) as string | undefined;
};

export const parseLiveInputStatusUpdateMessage = (message: unknown): LiveInputStatusUpdatePayload | null => {
  if (!message || typeof message !== 'object') {
    return null;
  }

  const msg = message as { messageType?: string; data?: Record<string, unknown> };
  if (msg.messageType !== MessageType.Notification || !msg.data) {
    return null;
  }

  if (msg.data.eventType !== PushNotificationEventType.InputStatusUpdate) {
    return null;
  }

  const inputId = (msg.data.id ?? msg.data.inputId) as string | undefined;
  if (!inputId) {
    return null;
  }

  return {
    inputId,
    overallStatus: readOverallStatus(msg.data),
  };
};

export const resolveLiveInputStatusUpdate = async (
  payload: LiveInputStatusUpdatePayload
): Promise<InputLiveStatusResponse & { inputId: string }> => {
  if (payload.overallStatus != null) {
    return {
      inputId: payload.inputId,
      overallStatus: payload.overallStatus,
    };
  }

  const response = (await FuelToolsApiService.getInputStreamStatus(payload.inputId)) as Record<string, unknown> | null;
  return {
    inputId: payload.inputId,
    overallStatus: readOverallStatus(response ?? undefined),
    liveProgramIds: (response?.liveProgramIds as string[] | undefined) ?? undefined,
    currentLiveProgramIds: (response?.currentLiveProgramIds as string[] | undefined) ?? undefined,
  };
};

/** Map API overallStatus to badge variant. Recording displays as Live. */
export const toSourceBadgeStatus = (overallStatus?: string | null): string | undefined => {
  const normalized = overallStatus?.toLowerCase().trim();
  if (!normalized) {
    return undefined;
  }

  if (normalized === 'recording') {
    return 'live';
  }

  if (normalized === 'connected') {
    return 'connected';
  }

  if (normalized === 'disconnected' || normalized === 'no connection') {
    return 'disconnected';
  }

  if (normalized === 'error' || normalized === 'failed') {
    return 'error';
  }

  // Live program schedule states (LiveProgramDto.ScheduleDisplay.Status).
  if (normalized === 'scheduled') {
    return 'scheduled';
  }

  if (normalized === 'notscheduled' || normalized === 'not scheduled') {
    return 'notscheduled';
  }

  return undefined;
};
